import React from 'react'
import LoginLayout from './LoginLayout'
import { OTP } from 'containers/MFA/OTP'
import { Button, Flex, sendNotification } from 'components'
import { useVerifyMFAMutation } from 'services/auth/mfa'
import { Navigate, useNavigate } from 'react-router'
import { useLazyGetAuthQuery } from 'services/auth'
import { useDispatch } from 'react-redux'
import { changeClientId } from 'services/slices/ui'
import { useSetLanguageMutation } from 'services/common'
import i18n from 'utils/i18n'
import { useSelector } from 'react-redux'
import { selectToken } from 'services/slices/auth'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'utils/useTranslation'
import { MFAError } from 'containers/MFA/ActivateMFAModal'

const schema = z.object({
  otp: z.string().min(6),
})

type FormInputs = z.infer<typeof schema>

export const MFAConfirmation: React.FC = () => {
  const { t } = useTranslation('pages.mfa')
  const token = useSelector(selectToken)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [setLanguage] = useSetLanguageMutation()

  const methods = useForm<FormInputs>({
    resolver: zodResolver(schema),
  })

  const [verifyMFA] = useVerifyMFAMutation()
  const [getAuth] = useLazyGetAuthQuery()

  const onSubmit = (data: FormInputs) => {
    verifyMFA({ type: 'totp', value: data.otp })
      .unwrap()
      .then(async () => {
        const response = await getAuth()
        if (response.isSuccess) {
          const isCustomerPortalUser =
            response.data?.user?.customer_portal_credentials?.client_id &&
            response.data?.user?.customer_portal_credentials?.entity_id
          const customerPortalUrl = `/engagement/customer_portal/${response.data?.user?.customer_portal_credentials?.entity_id}`
          response.data?.user?.as_client_id &&
            dispatch(changeClientId(String(response.data?.user?.as_client_id)))
          setLanguage(i18n.language)
          navigate(isCustomerPortalUser ? customerPortalUrl : '/')
        }
      })
      .catch((error) => {
        const errorCode = (error as any)?.data?.error_code
        if (Object.values(MFAError).includes(errorCode as MFAError)) {
          sendNotification({
            type: 'error',
            toastContent: t(`errors.${errorCode}`),
          })
        } else {
          sendNotification({
            type: 'error',
            toastContent: t('errors.something_went_wrong'),
          })
        }
      })
  }

  if (!token) {
    return <Navigate to="/" />
  }

  return (
    <LoginLayout title={t('enter_one_time_password')} subtitle={t('enter_description')}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Flex directionColumn="zero" gap={1}>
          <Flex center="zero" fullWidth>
            <Controller
              control={methods.control}
              name="otp"
              render={({ field: { onChange, value } }) => <OTP onChange={onChange} value={value} />}
            />
          </Flex>
          <Button type="submit">{t('submit_code')}</Button>
        </Flex>
      </form>
    </LoginLayout>
  )
}
